import { HttpApiService } from "@/shared/services/httpApi.service";

import { WALLETS_API_ENDPOINTS } from "../constants/wallets.constants";
import type {
  AdjustWalletBalancePayload,
  AdjustWalletBalanceResponse,
  CardHoldingWallet,
  OrganizationWalletAggregates,
  OrganizationWalletBalanceResponse,
  WalletTransactionFilters,
  WalletTransactionsResponse,
} from "../types/wallets-api.types";

export interface CardWalletActivationResponse {
  card_wallet_enabled: boolean;
  /** False when it was already on. */
  changed: boolean;
}

export class WalletService {
  /** The caller's organization turns its own card wallet on. Idempotent; turning it off is staff's. */
  static async enableCardWallet() {
    return HttpApiService.post<CardWalletActivationResponse>(
      `${WALLETS_API_ENDPOINTS.WALLETS}/card-wallet/enable`,
      {},
    );
  }

  static async getOrganizationCardWalletBalance(organizationId: string) {
    return HttpApiService.get<OrganizationWalletBalanceResponse>(
      `${WALLETS_API_ENDPOINTS.WALLETS}/organizations/${organizationId}/card-wallet/balance`,
    );
  }

  /**
   * The pool that funds the card program: where to deposit so Coloca can credit a card wallet, and what that
   * address accepts (staff only, `wallets:write` + INTERNAL).
   *
   * Takes no organization on purpose — one pool backs every organization's cards, so the answer is the same
   * for all of them.
   */
  static async getCardHoldingWallet() {
    return HttpApiService.get<CardHoldingWallet>(
      `${WALLETS_API_ENDPOINTS.WALLETS}/admin/card-holding-wallet`,
    );
  }

  static async getOrganizationWalletAggregates(organizationId: string) {
    return HttpApiService.get<OrganizationWalletAggregates>(
      `${WALLETS_API_ENDPOINTS.WALLETS}/organizations/${organizationId}/aggregates`,
    );
  }

  // WalletTransaction captures top-ups / transfers / fees; direct card
  // spend lives in CardTransaction.
  static async getWalletTransactions(walletId: string, filters: WalletTransactionFilters = {}) {
    return HttpApiService.get<WalletTransactionsResponse>(
      `${WALLETS_API_ENDPOINTS.WALLETS}/${walletId}/transactions`,
      { params: filters },
    );
  }

  static async getOrganizationCardWalletMovements(
    organizationId: string,
    filters: WalletTransactionFilters = {},
  ) {
    const balanceRes = await this.getOrganizationCardWalletBalance(organizationId);
    const walletId = balanceRes.data.wallet.id;
    return this.getWalletTransactions(walletId, filters);
  }

  /**
   * Manual credit / debit of a Cryptomate card funding wallet (staff only, `wallets:write` + INTERNAL).
   *
   * `external_reference` is the real deduplication key — the backend derives the movement's
   * `provider_transaction_id` from it, so replaying one answers 409 and moves no money. The `Idempotency-Key`
   * header is the second layer, for an exact retry of a request whose answer was lost.
   */
  static async adjustWalletBalance(
    walletId: string,
    payload: AdjustWalletBalancePayload,
    idempotencyKey: string,
  ) {
    return HttpApiService.post<AdjustWalletBalanceResponse>(
      `${WALLETS_API_ENDPOINTS.WALLETS}/${walletId}/adjust-balance`,
      payload,
      { headers: { "Idempotency-Key": idempotencyKey } },
    );
  }
}
